import React, { useEffect, useState } from 'react';
import disclaimerService from '../../services/disclaimerService';

const FALLBACK_TEXT =
  'Do not enter patient identifiers (names, MRN, room numbers, etc.). This platform is for analysis of generic, de-identified cases only.';

/**
 * Compact amber warning strip shown on the login page (pre-credentials).
 * Reminds users not to submit PHI before they have even typed anything.
 *
 * Text is fetched from the public /auth/disclaimer/platform-banner/ endpoint;
 * falls back to a built-in default if the endpoint is unreachable.
 */
export const PreLoginDisclaimerBanner: React.FC<{
  className?: string;
  variant?: 'light' | 'dark';
}> = ({ className, variant = 'light' }) => {
  const [text, setText] = useState<string>(FALLBACK_TEXT);
  const [enabled, setEnabled] = useState<boolean>(true);

  useEffect(() => {
    let cancelled = false;
    disclaimerService
      .fetchPlatformBanner()
      .then((resp) => {
        if (cancelled) return;
        setEnabled(!!resp.enabled);
        if (resp.banner_text) setText(resp.banner_text);
      })
      .catch(() => {
        // Keep fallback text on failure.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!enabled) return null;

  const isDark = variant === 'dark';

  return (
    <div
      className={`flex items-start gap-2.5 p-3 rounded-xl border ${
        isDark
          ? 'bg-amber-400/10 border-amber-400/25 backdrop-blur-sm'
          : 'bg-amber-50 border-amber-200'
      } ${className ?? ''}`}
      role="note"
      aria-label="Confidentiality notice"
    >
      <span
        className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full text-[11px] font-bold leading-none ${
          isDark
            ? 'bg-amber-400/20 text-amber-200 ring-1 ring-amber-300/30'
            : 'bg-amber-100 text-amber-700'
        }`}
        aria-hidden="true"
      >
        !
      </span>
      <p className={`text-xs leading-snug ${isDark ? 'text-amber-100/90' : 'text-amber-900'}`}>
        <span className={`font-semibold ${isDark ? 'text-amber-200' : ''}`}>Confidentiality: </span>
        {text}
      </p>
    </div>
  );
};

export default PreLoginDisclaimerBanner;
